/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Autocomplete,
  Badge,
  Box,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { authActions } from 'modules/common/auth/slice';
import { dashboardActions } from 'modules/dashboard/slice';
import { usersActions } from 'modules/users/slice';
import { configureEventCategoriesActions } from 'modules/event-configuration/slice';
import {
  selectHotelDropdownList,
  selectHotelName,
  selectIsSignedIn,
  selectHotelGroupDropdownList,
  selectEnableHotelGroups,
  selectMergedHotelList,
  selectHotelGroups,
  selectIsDemoUser,
  selectPredefinedHotelGroups,
  selectIsDemoModeActivated,
  selectIsPrimaryHotelSetOverTimeDisabled,
  selectInvalidPrimaryHotelList,
  selectIsPrimaryHotelSetOverTime,
  selectTabValue,
  selectTabList,
  selectIsHistoricalViOverlayActive,
} from 'modules/dashboard/selectors';
import { useEffect, useState } from 'react';
import { getHotelValuesFromList } from 'modules/common/utils/array';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { UiController, events } from 'modules/common/ui-controller';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { getHotelDemoModeFocusOnValue, toDate } from 'modules/dashboard/functions';
import ORDER_BY from 'modules/common/constants/order-by';
import { ADMIN_ROLE, DEMO_ROLE, SUPER_ADMIN_ROLE } from 'modules/common/constants/roles';
import ROUTES from 'modules/common/constants/route';
import { ENVIRONMENT } from 'config';
import AddHotelGroup from 'modules/common/components/add-hotel-group/components/add-hotel-group-view';
import { Apartment, Assistant } from '@mui/icons-material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { faroSendHotelBasedUserActivity } from 'modules/common/utils/faro';
import { updateScenarioFilter } from 'modules/dashboard/components/tab-container/search/functions';
import { searchActions } from 'modules/dashboard/components/tab-container/search/slice';
import {
  selectSearchData,
  selectSelectedScenario,
} from 'modules/dashboard/components/tab-container/search/selectors';
import { TextField, CustomEditIcon, CustomInfoIcon, HISTORICAL_VI_BLUR_STYLE } from './style';
import ViewHotelMetadata from '../hotel-metadata/view-hotel-metadata';
import ViewHotelSetOverTime from '../hotel-set-over-time';
import { useSetSecondaryHotelSetOverTime } from '../../../dashboard/utils/hotel-set-functions';

/**
 * Navigation bar hotel dropdown common component that uses
 * across modules for display hotels dropdown and select hotels
 */
const NavbarHotelDropdown = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  //
  const selectedHotelName = useSelector(selectHotelName);
  const organizationId = useSelector(selectOrganizationId);
  const hotelList = useSelector(selectHotelDropdownList);
  const groupsList = useSelector(selectHotelGroupDropdownList);
  const isSignIn = useSelector(selectIsSignedIn);
  const authUser = useSelector(selectAuthUser);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const selectedHotelGroups = useSelector(selectHotelGroups);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const predefinedHotelGroups = useSelector(selectPredefinedHotelGroups);
  const isHotelSetOverTimeDisabled = useSelector(selectIsPrimaryHotelSetOverTimeDisabled);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const hotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const tabValue = useSelector(selectTabValue);
  const isHistoricalViOverlayActive = useSelector(selectIsHistoricalViOverlayActive);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const selectedScenario = useSelector(selectSelectedScenario);
  const scenarios = useSelector(selectSearchData);
  //
  const setSecondaryHotelSetOverTime = useSetSecondaryHotelSetOverTime();
  //
  const [hotelCommand, setHotelCommand] = useState(null);
  const [hotelGroupCommand, setHotelGroupCommand] = useState(null);
  const [open, setOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [modifiedList, setModifiedList] = useState([]);
  const [addHotelGroupList, setAddHotelGroupList] = useState([]);
  const [showHotelMetadata, setShowHotelMetadata] = useState(false);
  const [showHotelSetOverTime, setShowHotelSetOverTime] = useState(false);
  //
  const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
  // get hotel list
  useEffect(() => {
    if (!isSignIn && serializedState) {
      return;
    }
    const query = `limit=${PAGINATION.HOTELS_PER_PAGE_COUNT}&sortBy=latestBookingDate:${ORDER_BY.DESCENDING}`;
    dispatch(
      dashboardActions.getHotelList({
        organizationId,
        query,
      })
    );
    dispatch(
      authActions.getOrganization({
        organizationId,
      })
    );
  }, []);
  // Triggered when tabValue changes
  useEffect(() => {
    setSecondaryHotelSetOverTime(hotelSetOverTime);
  }, [tabValue]);
  //
  useEffect(() => {
    if (authUser && organizationId) {
      const query = `limit=${PAGINATION.HOTEL_GROUPS_PER_PAGE_COUNT}&page=0`;
      dispatch(
        dashboardActions.getHotelGroupList({
          organizationId,
          userId: authUser?.id,
          query,
        })
      );
      const query2 = `limit=${PAGINATION.AGGREGATION_FILTER_PER_PAGE}&page=0`;
      dispatch(
        dashboardActions.getLoadHotelGroupList({
          organizationId,
          userId: authUser?.id,
          query: query2,
        })
      );
      dispatch(
        dashboardActions.getCommandFilterDropDownList({
          organizationId,
          userId: authUser?.id,
          query: `limit=${PAGINATION.COMMAND_FILTERS_PER_PAGE}&page=0`,
        })
      );
      dispatch(
        dashboardActions.getAssignedHotelGroupList({
          organizationId,
          userId: authUser?.id,
        })
      );
      dispatch(
        authActions.getSchemaMapping({
          organizationId,
        })
      );
    }
  }, [authUser, organizationId]);
  // get selected hotel , set pms date and set selected hotel for configure event categories
  useEffect(() => {
    const hotelData = mergedHotelList?.filter((hotel) => hotel?.id === selectedHotelName?.id);
    if (!enableHotelGroups && hotelData && hotelData.length > 0 && hotelData[0]?.pmsDate) {
      dispatch(dashboardActions.setPmsDate(toDate(new Date(hotelData[0]?.pmsDate))));
    } else if (enableHotelGroups && hotelData && hotelData[0]?.hotels?.length > 0) {
      const pmsDates = hotelData[0]?.hotels?.map((hotel) => {
        if (hotel?.pmsDate) {
          return toDate(new Date(hotel?.pmsDate));
        }
        return null;
      });
      dispatch(dashboardActions.setGroupPmsDates(pmsDates));
    } else {
      dispatch(dashboardActions.setPmsDate(null));
      dispatch(dashboardActions.setGroupPmsDates(null));
    }
    dispatch(configureEventCategoriesActions.setHotelId(selectedHotelName?.id));
  }, [selectedHotelName, hotelList, groupsList, enableHotelGroups]);
  // calculate no of rooms per hotel/hotel set
  useEffect(() => {
    if (selectedHotelName !== null && enableHotelGroups) {
      setIsEditable(true);
    } else if (selectedHotelName !== null) {
      setIsEditable(false);
    }
  }, [selectedHotelName, enableHotelGroups]);

  /* Temporarily commented out as the filter forecast dropdown is not in use. */
  // checked difference in start and end date in stay date range and get forecast filters
  // useEffect(() => {
  //   if (selectedHotelName) {
  //     const hotelId = selectedHotelName?.id;
  //     if (
  //       differenceInCalendarDays(new Date(targetDate.startDate), new Date(targetDate.endDate)) === 0
  //     ) {
  //       const query = `limit=${PAGINATION.FORECAST_DATA}&stayDate=${formatDate(
  //         targetDate.startDate,
  //         YEAR_MONTH_DAY_PATTERN
  //       )}`;
  //       dispatch(dashboardActions.getForecastFilters({ organizationId, hotelId, query }));
  //     } else {
  //       const query = `limit=${PAGINATION.FORECAST_DATA}&stayDate=${formatDate(
  //         targetDate.startDate,
  //         YEAR_MONTH_DAY_PATTERN
  //       )}&toStayDate=${formatDate(targetDate.endDate, YEAR_MONTH_DAY_PATTERN)}`;
  //       dispatch(dashboardActions.getForecastFilters({ organizationId, hotelId, query }));
  //     }
  //   } else {
  //     dispatch(dashboardActions.setForecastFilters([]));
  //   }
  // }, [selectedHotelName, targetDate]);

  // execute set hotel ui controller command flow
  useEffect(() => {
    if (hotelCommand !== null && hotelList?.length > 0 && selectedHotelName?.id) {
      const hotelsSelected = getHotelValuesFromList(hotelCommand, hotelList);

      if (hotelsSelected?.length > 0) {
        const targetHotel = hotelsSelected[0];
        dispatch(dashboardActions.setEnableHotelGroups(false));
        dispatch(dashboardActions.setHotelName(targetHotel));

        if (selectedHotelName.id === targetHotel.id) {
          dispatch(dashboardActions.setHotelChange(true));
          setHotelCommand(null);
        }
      } else {
        dispatch(dashboardActions.setHotelChange(true));
        setHotelCommand(null);
      }
    }
  }, [hotelCommand, hotelList?.length, selectedHotelName]);

  // execute set hotel ui controller command flow
  useEffect(() => {
    if (
      hotelGroupCommand !== null &&
      (groupsList?.length > 0 || predefinedHotelGroups?.length > 0) &&
      selectedHotelName?.id
    ) {
      const hotelGroupsSelected = getHotelValuesFromList(hotelGroupCommand, [
        ...groupsList,
        ...predefinedHotelGroups,
      ]);

      if (hotelGroupsSelected?.length > 0) {
        const targetHotelGroup = hotelGroupsSelected[0];
        dispatch(dashboardActions.setEnableHotelGroups(true));
        dispatch(dashboardActions.setHotelName(targetHotelGroup));
        if (selectedHotelName?.id === targetHotelGroup?.id) {
          dispatch(dashboardActions.setHotelChange(true));
          setHotelGroupCommand(null);
        }
      } else {
        dispatch(dashboardActions.setHotelChange(true));
        setHotelGroupCommand(null);
      }
    }
  }, [hotelGroupCommand, groupsList?.length, predefinedHotelGroups?.length, selectedHotelName]);
  // get set hotel command value
  const setHotelHandler = (data) => {
    setHotelCommand(data);
  };
  // get set hotel command value
  const setHotelGroupHandler = (data) => {
    setHotelGroupCommand(data);
  };
  // subscribe ui controller events
  useEffect(() => {
    UiController.subscribe(events.SET_HOTEL, setHotelHandler);
    return () => {
      UiController.unsubscribe(events.SET_HOTEL, setHotelHandler);
    };
  }, []);
  //
  useEffect(() => {
    UiController.subscribe(events.SET_HOTEL_GROUP, setHotelGroupHandler);
    return () => {
      UiController.unsubscribe(events.SET_HOTEL_GROUP, setHotelGroupHandler);
    };
  }, []);
  //
  const handleAddHotelToFavorites = (e, option) => {
    if (groupsList?.some((group) => group?.label === option?.label)) {
      const favoriteHotelSets = groupsList
        ?.map(() => {
          const matchingGroup = selectedHotelGroups?.results?.find(
            ({ hotelGroups }) => hotelGroups && hotelGroups?.hotelGroupName === option?.id
          );
          return matchingGroup
            ? {
                hotelGroups: [
                  {
                    hotelQuery: matchingGroup?.hotelGroups?.hotelQuery,
                    hotelGroupName: matchingGroup?.hotelGroups?.hotelGroupName,
                    isFavorite: !option?.isFavorite,
                  },
                ],
                userId: authUser?.id,
                organizationId,
              }
            : null;
        })
        ?.filter(Boolean)[0];
      dispatch(dashboardActions.addHotelGroupToFavorites(favoriteHotelSets));
    } else {
      dispatch(
        dashboardActions.addHotelToFavorite({
          organizationId,
          userId: authUser?.id,
          hotelId: option?.id,
        })
      );
    }
  };
  //
  useEffect(() => {
    if (enableHotelGroups) {
      setIsEditable(!selectedHotelName?.isPredefined);
    }
  }, [selectedHotelName, enableHotelGroups]);
  //
  const modifyOptions = (options) => {
    const modifiedOptions = [];

    options?.forEach((option) => {
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 3 });
      } else if (!option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 1 });
      }
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 1 });
      }
      if (option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 2 });
      }
    });

    // Sort options first by group, then by label
    return modifiedOptions?.sort((a, b) => {
      if (a?.group === b?.group) {
        if (isDemoUser || (!isDemoUser && isDemoModeActivated)) {
          return Number(a?.label?.split(' ')[1]) - Number(b?.label?.split(' ')[1]);
        }
        return a?.label?.localeCompare(b?.label);
      }
      return b.group - a.group;
    });
  };
  //

  useEffect(() => {
    if (mergedHotelList?.length > 0) {
      const modifiedOptions = modifyOptions(mergedHotelList);
      setModifiedList(modifiedOptions);
    }
  }, [mergedHotelList, isDemoUser, isDemoModeActivated]);
  //
  const isValidHotelLabel = (ff) => {
    const hotelPattern = /^Hotel \d+$/;
    return hotelPattern.test(ff.label);
  };
  //
  useEffect(() => {
    if (hotelList !== null && hotelList?.length > 0) {
      setAddHotelGroupList([...hotelList]);
      if (!isDemoUser && isDemoModeActivated && !isValidHotelLabel(hotelList[0])) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...hotelList]);
        setAddHotelGroupList(demoList);
      }
      if (
        !isDemoUser &&
        !isDemoModeActivated &&
        addHotelGroupList !== null &&
        addHotelGroupList?.length > 0
      ) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...addHotelGroupList]);
        setAddHotelGroupList(demoList);
      }
    }
  }, [isDemoModeActivated, hotelList]);
  // visual interpreter drawer is only mounted in the dashboard
  const isVisualInterpreterVisible =
    ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' &&
    location.pathname === ROUTES.DASHBOARD;
  //
  const handleOpenVisualAgent = () => {
    dispatch(dashboardActions.setOpenVisualAgentModal(true));
    dispatch(usersActions.setInterpreterAgentMessage(''));
  };
  //
  return (
    <Grid container flexDirection="row" justifyContent="flex-end" alignItems="center" wrap="nowrap">
      <div className="primary-hotel-dropdown">
        <Autocomplete
          id="primary-hotel-dropdown"
          size="small"
          sx={{
            minWidth: {
              md: 180,
              lg:
                authUser &&
                (authUser?.roles?.[0]?.name === ADMIN_ROLE ||
                  authUser?.roles?.[0]?.name === SUPER_ADMIN_ROLE)
                  ? 280
                  : 300,
            },
            '& .MuiOutlinedInput-root': {
              color: '#e5e5e5',
            },
            '& .MuiSvgIcon-root': {
              color: '#e5e5e5',
              width: '2rem',
              height: '2rem',
              top: 0,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiAutocomplete-input': {
              fontSize: { md: 13, lg: 15 },
              fontWeight: 'bold',
              // hide the selected hotel while a historical VI result is open
              ...(isHistoricalViOverlayActive && HISTORICAL_VI_BLUR_STYLE),
            },
            '& .MuiAutocomplete-endAdornment': {
              top: 'calc(50% - 0px)',
            },
          }}
          options={modifiedList ?? []}
          getOptionLabel={(option) => (option.label ? option.label : '')}
          groupBy={(option) =>
            // eslint-disable-next-line no-nested-ternary
            option.group === 3 ? 'Favorites' : option.group === 2 ? 'Predefined' : 'NonFavorites'
          }
          renderOption={(props, option) => {
            const { key, id, ...otherProps } = props;
            return (
              <Grid
                id={`hotel-id-${option?.id}`}
                key={`${option?.id}-${option?.isPredefined}`}
                container
                justifyContent="space-between"
                direction="row"
              >
                <Grid item xs={11}>
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...otherProps}>
                    {option.label}
                    {!option.isPredefined &&
                      groupsList?.some((group) => group.label === option?.label) && (
                        <IconButton>
                          <Tooltip title="Hotel Set">
                            <Apartment />
                          </Tooltip>
                        </IconButton>
                      )}
                    {option.isPredefined && (
                      <IconButton>
                        <Tooltip title="Hotel Set">
                          <Apartment />
                        </Tooltip>
                      </IconButton>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  {!option.isPredefined && (
                    <IconButton
                      key={`${option?.id}-${option?.isPredefined}-icon`}
                      onClick={(e) => handleAddHotelToFavorites(e, option)}
                    >
                      <Tooltip
                        title={option.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {option.isFavorite ? (
                          <StarIcon sx={{ height: 16, width: 16 }} />
                        ) : (
                          <StarBorderIcon sx={{ height: 16, width: 16 }} />
                        )}
                      </Tooltip>
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            );
          }}
          renderGroup={(params) => {
            const sortedGroups = ['Favorites', 'Predefined', 'NonFavorites'].map((groupName) => {
              if (params.group === groupName && params?.children?.length > 0) {
                return (
                  <Grid key={`Groups-${params.group}`}>
                    <Grid key="Favorites">
                      {groupName === 'Favorites' && params?.children?.length > 0 && (
                        <>
                          {params.children}
                          <br />
                          <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                          <br />
                        </>
                      )}
                    </Grid>
                    <Grid key="Predefined">
                      {groupName === 'Predefined' && params?.children?.length > 0 && (
                        <>
                          <Typography paddingLeft={2} variant="subtitle2">
                            Pre-Defined Hotel Sets
                          </Typography>
                          {params.children}
                          <br />
                          <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                          <br />
                        </>
                      )}
                    </Grid>
                    <Grid key="NonFavorites">
                      {groupName === 'NonFavorites' &&
                        params?.children?.length > 0 &&
                        params.children}
                    </Grid>
                  </Grid>
                );
              }
              return null;
            });

            // Filter out null values and render sorted groups
            return sortedGroups.filter((group) => group !== null);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, value) => {
            // Send user activity based on hotel every time user select a different hotel
            faroSendHotelBasedUserActivity(value);
            dispatch(dashboardActions.setHotelName(value));
            dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(false));
            dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(false));
            dispatch(dashboardActions.setOriginalHotelName(value));
            //
            if (tabValue === TAB_VALUE.SEARCH && selectedScenario) {
              const { updatedScenarios } = updateScenarioFilter({
                scenario: selectedScenario,
                filterKey: 'Hotel',
                value: value?.label,
                keyword: value?.label,
                allScenarios: scenarios,
              });
              //
              dispatch(searchActions.setSearchScenario(updatedScenarios));
            }
          }}
          disableClearable
          value={selectedHotelName ?? []}
          renderInput={(params) => <TextField {...params} size="small" color="primary" />}
          componentsProps={{
            paper: {
              sx: {
                minWidth: 350,
                '& .MuiAutocomplete-listbox': { overflowX: 'hidden' },
              },
            },
          }}
        />
      </div>
      <Tooltip
        title={
          selectedHotelName && 'hotels' in selectedHotelName
            ? 'Hotel Sets - Same Store'
            : 'Hotel Metadata'
        }
      >
        <span>
          <IconButton
            color="primary"
            variant="contained"
            onClick={() =>
              !(selectedHotelName && 'hotels' in selectedHotelName)
                ? setShowHotelMetadata(true)
                : setShowHotelSetOverTime(true)
            }
            disabled={
              isHotelSetOverTimeDisabled ||
              (selectedHotelName &&
                !('hotels' in selectedHotelName) &&
                (isDemoModeActivated || authUser?.roles?.[0]?.name === DEMO_ROLE))
            }
            sx={{
              '&.Mui-disabled': {
                background: 'transparent',
              },
              ml: 1,
              mb: 0.5,
            }}
          >
            <Badge
              color="error"
              variant="dot"
              invisible={
                isHotelSetOverTimeDisabled || !(selectedHotelName && 'hotels' in selectedHotelName)
              }
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <CustomInfoIcon size="15px" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Add/Edit Hotel Set">
        <span className="add-edit-hotel-set">
          <IconButton
            color="primary"
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              '&.Mui-disabled': {
                background: 'transparent', // 'gray',
                //  color: 'white',
              },
              ml: 1,
              mb: 0.5,
              zIndex: 1000,
            }}
          >
            <CustomEditIcon size="15px" />
          </IconButton>
        </span>
      </Tooltip>
      {isVisualInterpreterVisible && (
        <Tooltip title="Visual Interpreter Agent">
          <span className="visual-interpreter-agent">
            <IconButton
              color="primary"
              variant="contained"
              onClick={handleOpenVisualAgent}
              sx={{
                ml: 1,
                mb: 0.5,
                zIndex: 1000,
              }}
            >
              <Assistant sx={{ color: 'white', fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <AddHotelGroup
        open={open}
        onClose={() => setOpen(false)}
        hotelList={addHotelGroupList ?? []}
        selectedHotelGroupName={selectedHotelName}
        isEditable={isEditable}
        enableHotelGroups={enableHotelGroups}
        isPrimary
      />
      {selectedHotelName &&
        (!('hotels' in selectedHotelName) || !selectedHotelName.isPredefined) && (
          <ViewHotelMetadata
            open={showHotelMetadata}
            hotelList={addHotelGroupList ?? []}
            onClose={() => {
              setShowHotelMetadata(false);
              // dispatch(notificationActions.resetNotification());
            }}
            selectedHotelName={
              !enableHotelGroups && !selectedHotelName?.isPredefined && selectedHotelName
            }
          />
        )}
      {selectedHotelName && 'hotels' in selectedHotelName && !selectedHotelName.isPredefined && (
        <ViewHotelSetOverTime
          open={showHotelSetOverTime}
          onClose={() => {
            setShowHotelSetOverTime(false);
          }}
          selectedHotels={selectedHotelName}
          invalidPrimaryHotels={invalidPrimaryHotels}
          hotelSetOverTime={hotelSetOverTime}
          handleHotelSetChange={(event) => {
            dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(event.target.checked));
            setSecondaryHotelSetOverTime(event.target.checked);
          }}
        />
      )}
    </Grid>
  );
};
//
export default NavbarHotelDropdown;
